import { Add, AutoAwesome, Delete, Edit } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  createFilterOptions,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { startCase } from "lodash";
import { useCallback, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useFlags } from "launchdarkly-react-client-sdk";
import { medicalsApi } from "../../../../../../../api/medicalsApi";
import { useNotifications } from "../../../../../../../components/dashboard/NotificationsProvider";
import { usePractitionerReference } from "../../../../../../../hooks/usePractitioner";
import { useUnsavedWhileEditing } from "../../../../../../../hooks/useUnsavedWhileEditing";
import { CopilotManager } from "../../../../../../../managers/CopilotManager";
import { contraceptionMethods } from "../../../../../../../slices/options/sexual";
import {
  FamilyHistoryItem,
  TCopilot,
  TCopilotCarePlan,
  TCopilotDiagnosis,
  TCopilotDiagnosisClinicalStatus,
  TCopilotDiagnosisVerificationStatus,
  TCopilotNote,
  TCopilotPatientHistory,
  TCopilotPhysicalAssessment,
  TCopilotReviewOfSystems,
} from "../../../../../../../types/copilot";
import EditableSection from "./EditableSection";
import EditableAllergies from "./EditableAllergies";
import EditableVitals from "./EditableVitals";
import CopilotSectionEditedBadge from "./CopilotSectionEditedBadge";
import CopilotSectionAcceptButton from "./CopilotSectionAcceptButton";
import {
  HISTORY_POPULATE_APPLY_CONFIG,
  resolveHumanEditedSectionFromHistorySubsection,
  shouldShowSectionAcceptButton,
  withSectionHumanEditedExtension,
} from "../utils/copilotExtensions";
import { useCopilotSectionApply } from "../../hooks/useCopilotSectionApply";
import {
  useOrganization,
  useOrganizationReference,
} from "../../../../../../facility/hooks/useOrganization";
import { useExecutor } from "../../../../../../../hooks/useExecutor";
import { Reference } from "../../../../../../../types/reference";
import { QueryKeys } from "../../../../../../../constants/queryKeys";
import { useCopilotClinicalContext } from "../../useCopilotClinicalContext";

const VERIFICATION_STATUSES: TCopilotDiagnosisVerificationStatus[] = [
  "unconfirmed",
  "provisional",
  "differential",
  "confirmed",
  "refuted",
  "entered-in-error",
];

const CLINICAL_STATUSES: TCopilotDiagnosisClinicalStatus[] = [
  "active",
  "recurrence",
  "relapse",
  "inactive",
  "remission",
  "resolved",
];

// Older notes stored a single `status`; normalize any diagnosis into the split
// verificationStatus/clinicalStatus shape, defaulting to provisional/active.
const normalizeDiagnosis = (
  diagnosis: TCopilotDiagnosis,
): TCopilotDiagnosis => {
  const legacy = diagnosis.status;
  const verificationStatus =
    diagnosis.verificationStatus ??
    (VERIFICATION_STATUSES.includes(
      legacy as TCopilotDiagnosisVerificationStatus,
    )
      ? (legacy as TCopilotDiagnosisVerificationStatus)
      : "provisional");
  const clinicalStatus =
    diagnosis.clinicalStatus ??
    (CLINICAL_STATUSES.includes(legacy as TCopilotDiagnosisClinicalStatus)
      ? (legacy as TCopilotDiagnosisClinicalStatus)
      : "active");

  return { ...diagnosis, verificationStatus, clinicalStatus };
};

const extractDiagnosisDisplay = (diagnosis: TCopilotDiagnosis) => {
  const { code } = diagnosis;
  if (typeof code === "string") {
    return code;
  }
  if (code?.coding?.[0] !== undefined) {
    return code.coding[0].display;
  }
  return code?.text || "";
};

const extractDiagnosisText = (
  diagnoses: TCopilotDiagnosis[] | null,
  readOnly = false,
) => {
  const {
    copilotData,
    setCopilotData,
    encounterReference,
    patientReference,
    encounterHeaders,
    hasLinkedEncounter,
  } = useCopilotClinicalContext();
  const { setNotification } = useNotifications();
  const queryClient = useQueryClient();
  const { executeCallback } = useExecutor();
  const [isEditing, setIsEditing] = useState(false);
  useUnsavedWhileEditing("copilot-note-diagnoses", isEditing);
  const [editedDiagnoses, setEditedDiagnoses] = useState<TCopilotDiagnosis[]>(
    (diagnoses || []).map(normalizeDiagnosis),
  );
  const [isSaving, setIsSaving] = useState(false);

  const organizationReference = useOrganizationReference();
  const { countryCode } = useOrganization();

  // Helper function to handle populate actions with error handling, notifications, query invalidation, and card refresh
  const handlePopulate = async (
    populateFn: () => Promise<any>,
    entityType: string,
    queryKeysToInvalidate: string[] = [],
    refreshCallbackName?: string,
  ) => {
    if (!encounterReference?.identifier || !organizationReference?.identifier) {
      setNotification({
        message:
          "Link this note to a medical encounter to apply content to the chart.",
        variant: "error",
      });
      // Clear loading state if it was set in onClick
      return;
    }

    try {
      const result = await populateFn();

      // Invalidate relevant queries to refresh data
      if (queryKeysToInvalidate.length > 0) {
        await Promise.all(
          queryKeysToInvalidate.map((key) =>
            queryClient.invalidateQueries({ queryKey: [key] }),
          ),
        );
      }

      // Trigger card refresh callback if provided
      if (
        refreshCallbackName &&
        patientReference?.identifier &&
        encounterReference?.identifier
      ) {
        try {
          await executeCallback(
            refreshCallbackName,
            null,
            patientReference.identifier,
            encounterReference.identifier,
          );
        } catch (callbackError) {
          console.error(
            `Error executing refresh callback ${refreshCallbackName}:`,
            callbackError,
          );
        }
      }

      // Show success message with result details
      const created = result?.created || 0;
      const skipped = result?.skipped || result?.unchanged || 0;
      const updated = result?.updated || 0;

      let message = `${startCase(entityType)} populated successfully`;
      if (created > 0 || updated > 0 || skipped > 0) {
        const parts: string[] = [];
        if (created > 0) parts.push(`${created} created`);
        if (updated > 0) parts.push(`${updated} updated`);
        if (skipped > 0) parts.push(`${skipped} unchanged`);
        message += ` (${parts.join(", ")})`;
      }

      setNotification({
        message,
        variant: "success",
      });
    } catch (error: any) {
      console.error(`Error populating ${entityType}:`, error);
      setNotification({
        message: `Failed to populate ${entityType}: ${error?.message || "Unknown error"}`,
        variant: "error",
      });
      throw error;
    }
  };

  const handleSave = async () => {
    if (!copilotData?.id) return;

    try {
      setIsSaving(true);

      const updatedNote: TCopilot = withSectionHumanEditedExtension(
        {
          ...copilotData,
          note: {
            ...copilotData.note,
            diagnoses: editedDiagnoses,
          },
        },
        "diagnoses",
      );

      const response = await medicalsApi.updateCopilotNote(
        copilotData.id,
        updatedNote,
        encounterHeaders,
      );

      setCopilotData(response.data);
      setNotification({
        message: "Diagnoses updated successfully",
        variant: "success",
      });
      setIsEditing(false);
    } catch (error) {
      console.error(error);
      setNotification({
        message: "Failed to update diagnoses",
        variant: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const addNewDiagnosis = () => {
    setEditedDiagnoses([
      ...editedDiagnoses,
      {
        code: "",
        verificationStatus: "provisional",
        clinicalStatus: "active",
      },
    ]);
  };

  const removeDiagnosis = (index: number) => {
    setEditedDiagnoses(editedDiagnoses.filter((_, i) => i !== index));
  };

  const hasDiagnosisData =
    diagnoses && diagnoses.length > 0 && diagnoses.some((d) => d?.code);

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="h6">Diagnosis</Typography>
          <CopilotSectionEditedBadge section="diagnoses" />
          {!isEditing && !readOnly && (
            <IconButton size="small" onClick={() => setIsEditing(!isEditing)}>
              <Edit fontSize="small" />
            </IconButton>
          )}
        </Box>
        {!readOnly && !isEditing && hasLinkedEncounter && hasDiagnosisData && (
          <CopilotSectionAcceptButton
            sections={["diagnoses"]}
            populationStatusField="diagnoses"
            onAccept={() =>
              handlePopulate(
                () =>
                  CopilotManager.populateDiagnoses(
                    encounterReference?.identifier,
                    organizationReference?.identifier,
                    countryCode,
                  ),
                "diagnoses",
                [],
                "copilot_refresh_diagnoses",
              )
            }
          />
        )}
      </Box>

      {isEditing ? (
        <Box sx={{ mt: 1 }}>
          {editedDiagnoses?.map((diagnosis, index) => (
            <Box
              key={index}
              sx={{ display: "flex", gap: 1, mb: 1, alignItems: "flex-start" }}
            >
              <TextField
                size="small"
                label="Diagnosis"
                value={extractDiagnosisDisplay(diagnosis)}
                onChange={(e) => {
                  const newDiagnoses = [...editedDiagnoses];
                  newDiagnoses[index] = {
                    ...newDiagnoses[index],
                    code: e.target.value,
                  };
                  setEditedDiagnoses(newDiagnoses);
                }}
                sx={{ flexGrow: 1 }}
              />
              <TextField
                select
                size="small"
                label="Verification"
                value={diagnosis.verificationStatus ?? "provisional"}
                onChange={(e) => {
                  const newDiagnoses = [...editedDiagnoses];
                  newDiagnoses[index] = {
                    ...newDiagnoses[index],
                    verificationStatus: e.target
                      .value as TCopilotDiagnosisVerificationStatus,
                  };
                  setEditedDiagnoses(newDiagnoses);
                }}
                sx={{ width: 150 }}
              >
                {VERIFICATION_STATUSES?.map((status) => (
                  <MenuItem key={status} value={status}>
                    {startCase(status)}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="Clinical Status"
                value={diagnosis.clinicalStatus ?? "active"}
                onChange={(e) => {
                  const newDiagnoses = [...editedDiagnoses];
                  newDiagnoses[index] = {
                    ...newDiagnoses[index],
                    clinicalStatus: e.target
                      .value as TCopilotDiagnosisClinicalStatus,
                  };
                  setEditedDiagnoses(newDiagnoses);
                }}
                sx={{ width: 150 }}
              >
                {CLINICAL_STATUSES?.map((status) => (
                  <MenuItem key={status} value={status}>
                    {startCase(status)}
                  </MenuItem>
                ))}
              </TextField>
              <IconButton size="small" onClick={() => removeDiagnosis(index)}>
                <Delete fontSize="small" />
              </IconButton>
            </Box>
          ))}

          <Button
            size="small"
            startIcon={<Add />}
            onClick={addNewDiagnosis}
            sx={{ mt: 1 }}
          >
            Add Diagnosis
          </Button>

          <Box
            sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 2 }}
          >
            <Button
              size="small"
              onClick={() => {
                setEditedDiagnoses((diagnoses || []).map(normalizeDiagnosis));
                setIsEditing(false);
              }}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <LoadingButton
              size="small"
              variant="contained"
              onClick={handleSave}
              loading={isSaving}
            >
              Save
            </LoadingButton>
          </Box>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {diagnoses && diagnoses.length > 0
            ? diagnoses?.map((rawItem, index) => {
                const item = normalizeDiagnosis(rawItem);
                return (
                  <Box
                    key={index}
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <Typography variant="body2">
                      {extractDiagnosisDisplay(item)}
                    </Typography>
                    <Chip
                      label={startCase(item.verificationStatus)}
                      size="small"
                      color="info"
                      variant="outlined"
                      sx={{ height: "20px" }}
                    />
                    <Chip
                      label={startCase(item.clinicalStatus)}
                      size="small"
                      color="default"
                      variant="outlined"
                      sx={{ height: "20px" }}
                    />
                  </Box>
                );
              })
            : null}
        </Box>
      )}
    </Box>
  );
};

const formatValue = (value: any): string => {
  if (!value) return "";

  if (typeof value === "string") return value;
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") return String(value);

  if (Array.isArray(value)) {
    return value?.map((item) => formatValue(item)).join(", ");
  }

  if (typeof value === "object") {
    return Object.entries(value)
      .filter(([_, val]) => val !== null && val !== "")
      ?.map(([key, val]) => `${startCase(key)}: ${formatValue(val)}`)
      .join(", ");
  }

  return String(value);
};

const formatMedicationDetails = (med: any) => {
  const parts = [];

  if (med.medicationName) {
    parts.push(med.medicationName);
  }

  if (med.dosage?.value && med.dosage?.unit) {
    parts.push(`${med.dosage.value} ${med.dosage.unit}`);
  }

  if (med.route) {
    parts.push(med.route);
  }

  if (med.frequency) {
    parts.push(med.frequency);
  }

  if (med.quantity) {
    parts.push(`Qty: ${med.quantity}`);
  }

  if (med.instructions) {
    parts.push(`(${med.instructions})`);
  }

  return parts.join(" - ");
};

const extractPlanText = (plan: TCopilotCarePlan | null, readOnly = false) => {
  const {
    copilotData,
    setCopilotData,
    encounterReference,
    patientReference,
    encounterHeaders,
    hasLinkedEncounter,
  } = useCopilotClinicalContext();
  const { setNotification } = useNotifications();
  const [isEditing, setIsEditing] = useState(false);
  useUnsavedWhileEditing("copilot-note-plan", isEditing);
  const [editedPlan, setEditedPlan] = useState<TCopilotCarePlan>(
    plan || {
      details: null,
      prescriptions: [],
      followUp: null,
      tests: null,
      referrals: null,
      procedures: null,
    },
  );

  const [tests, setTests] = useState<string[]>(editedPlan?.tests || []);
  const [testsString, setTestsString] = useState(
    Array.isArray(tests) ? tests.join(",") : tests || "",
  );
  const [referralsString, setReferralsString] = useState(
    Array.isArray(editedPlan?.referrals)
      ? editedPlan.referrals.join(",")
      : editedPlan?.referrals || "",
  );
  const [proceduresString, setProceduresString] = useState(
    Array.isArray(editedPlan?.procedures)
      ? editedPlan.procedures.join(",")
      : editedPlan?.procedures || "",
  );
  const [isSaving, setIsSaving] = useState(false);

  const organizationReference = useOrganizationReference();
  const { countryCode } = useOrganization();
  const { executeCallback } = useExecutor();

  const handlePopulateCarePlan = async () => {
    if (!encounterReference?.identifier || !organizationReference?.identifier) {
      setNotification({
        message:
          "Link this note to a medical encounter to apply content to the chart.",
        variant: "error",
      });
      throw new Error("Encounter not linked");
    }

    try {
      const result = await CopilotManager.populateCarePlans(
        encounterReference.identifier,
        organizationReference.identifier,
        countryCode,
      );

      const created = result?.created;
      const updated = result?.updated;
      let message = "Care plan populated successfully";
      if (created || updated) {
        message = created
          ? "Care plan created successfully"
          : "Care plan updated successfully";
      }
      setNotification({ message, variant: "success" });
    } catch (error: any) {
      console.error("Error populating care plan:", error);
      setNotification({
        message: `Failed to populate care plan: ${error?.message || "Unknown error"}`,
        variant: "error",
      });
      throw error;
    }
  };

  const handleSave = async () => {
    if (!copilotData?.id) return;

    try {
      setIsSaving(true);

      const updatedNote: TCopilot = withSectionHumanEditedExtension(
        {
          ...copilotData,
          note: {
            ...copilotData.note,
            plan: editedPlan,
          },
        },
        "plan",
      );

      const response = await medicalsApi.updateCopilotNote(
        copilotData.id,
        updatedNote,
        encounterHeaders,
      );

      setCopilotData(response.data);
      setNotification({
        message: "Care plan updated successfully",
        variant: "success",
      });
      setIsEditing(false);
    } catch (error) {
      console.error(error);
      setNotification({
        message: "Failed to update care plan",
        variant: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="h6">Care Plan</Typography>
          <CopilotSectionEditedBadge section="plan" />
          {!isEditing && !readOnly && (
            <IconButton size="small" onClick={() => setIsEditing(!isEditing)}>
              <Edit fontSize="small" />
            </IconButton>
          )}
        </Box>
        {!isEditing && !readOnly && hasLinkedEncounter && plan && (
          <CopilotSectionAcceptButton
            sections={["plan"]}
            populationStatusField="carePlans"
            onAccept={handlePopulateCarePlan}
          />
        )}
      </Box>

      {isEditing ? (
        <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            fullWidth
            multiline
            minRows={2}
            size="small"
            label="Instructions"
            value={editedPlan.details || ""}
            onChange={(e) =>
              setEditedPlan((prev) => ({
                ...prev,
                details: e.target.value,
              }))
            }
          />

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Prescriptions
            </Typography>
            {editedPlan.prescriptions?.map((med, index) => (
              <Box
                key={index}
                sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}
              >
                <TextField
                  fullWidth
                  size="small"
                  label="Medication"
                  value={med.medicationName}
                  onChange={(e) => {
                    const newMeds = [...editedPlan.prescriptions];
                    newMeds[index] = {
                      ...newMeds[index],
                      medicationName: e.target.value,
                    };
                    setEditedPlan((prev) => ({
                      ...prev,
                      prescriptions: newMeds,
                    }));
                  }}
                />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Dosage"
                      value={med.dosage?.value}
                      onChange={(e) => {
                        const newMeds = [...editedPlan.prescriptions];
                        newMeds[index] = {
                          ...newMeds[index],
                          dosage: {
                            ...newMeds[index].dosage,
                            value: e.target.value,
                          },
                        };
                        setEditedPlan((prev) => ({
                          ...prev,
                          prescriptions: newMeds,
                        }));
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Unit"
                      value={med.dosage?.unit}
                      onChange={(e) => {
                        const newMeds = [...editedPlan.prescriptions];
                        newMeds[index] = {
                          ...newMeds[index],
                          dosage: {
                            ...newMeds[index].dosage,
                            unit: e.target.value,
                          },
                        };
                        setEditedPlan((prev) => ({
                          ...prev,
                          prescriptions: newMeds,
                        }));
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Frequency"
                      value={med.frequency}
                      onChange={(e) => {
                        const newMeds = [...(editedPlan?.prescriptions || [])];
                        newMeds[index] = {
                          ...newMeds[index],
                          frequency: e.target.value,
                        };
                        setEditedPlan((prev) => ({
                          ...prev,
                          prescriptions: newMeds,
                        }));
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Route"
                      value={med.route || ""}
                      onChange={(e) => {
                        const newMeds = [...(editedPlan?.prescriptions || [])];
                        newMeds[index] = {
                          ...newMeds[index],
                          route: e.target.value,
                        };
                        setEditedPlan((prev) => ({
                          ...prev,
                          prescriptions: newMeds,
                        }));
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Quantity"
                      value={med.quantity || ""}
                      onChange={(e) => {
                        const newMeds = [...(editedPlan?.prescriptions || [])];
                        newMeds[index] = {
                          ...newMeds[index],
                          quantity: e.target.value,
                        };
                        setEditedPlan((prev) => ({
                          ...prev,
                          prescriptions: newMeds,
                        }));
                      }}
                    />
                  </Grid>
                </Grid>

                <TextField
                  fullWidth
                  size="small"
                  label="Instructions"
                  value={med.instructions}
                  onChange={(e) => {
                    const newMeds = [...editedPlan.prescriptions];
                    newMeds[index] = {
                      ...newMeds[index],
                      instructions: e.target.value,
                    };
                    setEditedPlan((prev) => ({
                      ...prev,
                      prescriptions: newMeds,
                    }));
                  }}
                />
                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <IconButton
                    size="small"
                    onClick={() => {
                      const newMeds = editedPlan.prescriptions.filter(
                        (_, i) => i !== index,
                      );
                      setEditedPlan((prev) => ({
                        ...prev,
                        prescriptions: newMeds,
                      }));
                    }}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            ))}
            <Button
              size="small"
              startIcon={<Add />}
              onClick={() => {
                setEditedPlan((prev) => ({
                  ...prev,
                  prescriptions: [
                    ...(prev?.prescriptions || []),
                    {
                      medicationName: "",
                      dosage: {
                        value: null,
                        unit: null,
                      },
                      frequency: null,
                      instructions: null,
                      route: null,
                      quantity: null,
                    },
                  ],
                }));
              }}
            >
              Add Medication
            </Button>
          </Box>

          <TextField
            fullWidth
            multiline
            minRows={2}
            size="small"
            label="Follow-up"
            value={editedPlan.followUp || ""}
            onChange={(e) =>
              setEditedPlan((prev) => ({
                ...prev,
                followUp: e.target.value,
              }))
            }
          />

          <TextField
            fullWidth
            multiline
            minRows={2}
            size="small"
            label="Tests"
            value={testsString}
            onChange={(e) => {
              const inputValue = e.target.value;
              setTestsString(inputValue);
              const newTests = inputValue
                .split(",")
                .map((test) => test.trim())
                .filter(Boolean);
              setTests(newTests);
              setEditedPlan((prev) => ({
                ...prev,
                tests: newTests,
              }));
            }}
          />

          <TextField
            fullWidth
            multiline
            minRows={2}
            size="small"
            label="Referrals"
            value={referralsString}
            onChange={(e) => {
              const inputValue = e.target.value;
              setReferralsString(inputValue);
              const newReferrals = inputValue
                .split(",")
                .map((referral) => referral.trim())
                .filter(Boolean);
              setEditedPlan((prev) => ({
                ...prev,
                referrals: newReferrals,
              }));
            }}
          />

          <TextField
            fullWidth
            multiline
            minRows={2}
            size="small"
            label="Procedures"
            value={proceduresString}
            onChange={(e) => {
              const inputValue = e.target.value;
              setProceduresString(inputValue);
              const newProcedures = inputValue
                .split(",")
                .map((procedure) => procedure.trim())
                .filter(Boolean);
              setEditedPlan((prev) => ({
                ...prev,
                procedures: newProcedures,
              }));
            }}
          />

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button
              size="small"
              onClick={() => {
                setEditedPlan(plan);
                setIsEditing(false);
              }}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <LoadingButton
              size="small"
              variant="contained"
              onClick={handleSave}
              loading={isSaving}
            >
              Save
            </LoadingButton>
          </Box>
        </Box>
      ) : (
        <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 2 }}>
          <div>
            <Typography variant="subtitle2" fontWeight="bold">
              Instructions:
            </Typography>
            {plan?.details && (
              <Typography variant="body2">{plan.details}</Typography>
            )}
          </div>

          <div>
            <Typography variant="subtitle2" fontWeight="bold">
              Prescriptions:
            </Typography>
            {plan?.prescriptions && plan.prescriptions.length > 0 ? (
              <ul style={{ margin: 0, paddingLeft: "1.5rem" }}>
                {plan.prescriptions?.map((med, index) => (
                  <li key={index}>
                    <Typography variant="body2">
                      {formatMedicationDetails(med)}
                    </Typography>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>

          <div>
            <Typography variant="subtitle2" fontWeight="bold">
              Follow-up:
            </Typography>
            {plan?.followUp && (
              <Typography variant="body2">{plan.followUp}</Typography>
            )}
          </div>

          <div>
            <Typography variant="subtitle2" fontWeight="bold">
              Tests:
            </Typography>
            {plan?.tests && (
              <Typography variant="body2">
                {Array.isArray(plan?.tests)
                  ? plan?.tests.join(", ")
                  : plan?.tests}
              </Typography>
            )}
          </div>

          <div>
            <Typography variant="subtitle2" fontWeight="bold">
              Referrals:
            </Typography>
            {plan?.referrals && (
              <Typography variant="body2">
                {Array.isArray(plan?.referrals)
                  ? plan?.referrals.join(", ")
                  : plan?.referrals}
              </Typography>
            )}
          </div>

          <div>
            <Typography variant="subtitle2" fontWeight="bold">
              Procedures:
            </Typography>
            {plan?.procedures && (
              <Typography variant="body2">
                {Array.isArray(plan?.procedures)
                  ? plan?.procedures.join(", ")
                  : plan?.procedures}
              </Typography>
            )}
          </div>
        </Box>
      )}
    </Box>
  );
};

const extractHistoryText = (
  history: TCopilotPatientHistory | null,
  readOnly = false,
) => {
  const {
    copilotData,
    setCopilotData,
    encounterReference,
    patientReference,
    encounterHeaders,
    hasLinkedEncounter,
  } = useCopilotClinicalContext();
  const { setNotification } = useNotifications();
  const queryClient = useQueryClient();
  const { executeCallback } = useExecutor();
  const [editingSections, setEditingSections] = useState<
    Record<string, boolean>
  >({});
  const isHistoryEditing = useMemo(
    () => Object.values(editingSections).some(Boolean),
    [editingSections],
  );
  useUnsavedWhileEditing("copilot-note-history", isHistoryEditing);
  const [editedContent, setEditedContent] =
    useState<TCopilotPatientHistory>(history);
  const [isSaving, setIsSaving] = useState(false);
  const [populatingStates, setPopulatingStates] = useState<
    Record<string, boolean>
  >({});
  const [inputValue, setInputValue] = useState("");
  const [contraceptionInputValue, setContraceptionInputValue] = useState("");
  const organizationReference = useOrganizationReference();
  const { countryCode } = useOrganization();
  const practitionerReference = usePractitionerReference();
  const { clearAfterApply } = useCopilotSectionApply();
  const { enableAiNoteAcceptance } = useFlags();

  const shouldShowHistoryAccept = (entityType: string) => {
    if (!enableAiNoteAcceptance) {
      return false;
    }
    const applyConfig = HISTORY_POPULATE_APPLY_CONFIG[entityType];
    if (!applyConfig) {
      return true;
    }
    return shouldShowSectionAcceptButton(copilotData, applyConfig);
  };

  // Generic helper function to check if nested object has data
  const hasNestedData = (data: any): boolean => {
    if (!data) return false;
    return Object.values(data).some((value) => {
      if (value === null || value === undefined || value === "") return false;
      if (typeof value === "object" && !Array.isArray(value)) {
        return Object.values(value).some((v) => {
          if (v === null || v === undefined || v === "") return false;
          if (Array.isArray(v)) return v.length > 0;
          if (typeof v === "object") {
            return Object.values(v).some(
              (nested) =>
                nested !== null &&
                nested !== undefined &&
                nested !== "" &&
                (Array.isArray(nested) ? nested.length > 0 : true),
            );
          }
          return true;
        });
      }
      if (Array.isArray(value)) return value.length > 0;
      return true;
    });
  };

  // Helper function to check if social history has data
  const hasSocialHistoryData = (social: any): boolean => {
    return hasNestedData(social);
  };

  const hasMedicalHistoryData = (medical: any): boolean => {
    return hasNestedData(medical);
  };

  const hasFamilyHistoryData = (family: any): boolean => {
    return hasNestedData(family);
  };

  const hasImmunizationData = (immunizations: any): boolean => {
    return hasNestedData(immunizations);
  };

  const hasMedicationData = (medications: any): boolean => {
    return hasNestedData(medications);
  };

  const hasProcedureData = (procedures: any): boolean => {
    return hasNestedData(procedures);
  };

  const hasDiagnosisData = (diagnoses: any): boolean => {
    if (!diagnoses || !Array.isArray(diagnoses)) return false;
    return diagnoses.length > 0 && diagnoses.some((d) => d?.code);
  };

  // Helper function to handle populate actions with error handling, notifications, query invalidation, and card refresh
  const handlePopulate = useCallback(
    async (
      populateFn: () => Promise<any>,
      entityType: string,
      queryKeysToInvalidate: string[] = [],
      refreshCallbackName?: string,
    ) => {
      if (
        !encounterReference?.identifier ||
        !organizationReference?.identifier
      ) {
        setNotification({
          message:
            "Link this note to a medical encounter to apply content to the chart.",
          variant: "error",
        });
        // Clear loading state if it was set in onClick
        setPopulatingStates((prev) => ({ ...prev, [entityType]: false }));
        return;
      }

      // Ensure loading state is set (may already be set in onClick, but ensure it's set here too)
      setPopulatingStates((prev) => {
        const newState = { ...prev, [entityType]: true };
        return newState;
      });

      try {
        const result = await populateFn();

        // Invalidate relevant queries to refresh data
        if (queryKeysToInvalidate.length > 0) {
          await Promise.all(
            queryKeysToInvalidate.map((key) =>
              queryClient.invalidateQueries({ queryKey: [key] }),
            ),
          );
        }

        // Trigger card refresh callback if provided
        if (
          refreshCallbackName &&
          patientReference?.identifier &&
          encounterReference?.identifier
        ) {
          try {
            await executeCallback(
              refreshCallbackName,
              null,
              patientReference.identifier,
              encounterReference.identifier,
            );
          } catch (callbackError) {
            console.error(
              `Error executing refresh callback ${refreshCallbackName}:`,
              callbackError,
            );
          }
        }

        // Show success message with result details
        const created = result?.created || 0;
        const skipped = result?.skipped || result?.unchanged || 0;
        const updated = result?.updated || 0;

        let message = `${startCase(entityType)} populated successfully`;
        if (created > 0 || updated > 0 || skipped > 0) {
          const parts: string[] = [];
          if (created > 0) parts.push(`${created} created`);
          if (updated > 0) parts.push(`${updated} updated`);
          if (skipped > 0) parts.push(`${skipped} unchanged`);
          message += ` (${parts.join(", ")})`;
        }

        setNotification({
          message,
          variant: "success",
        });

        const applyConfig = HISTORY_POPULATE_APPLY_CONFIG[entityType];
        if (applyConfig) {
          await clearAfterApply(
            applyConfig.sections,
            applyConfig.populationStatusField,
          );
        }
      } catch (error: any) {
        console.error(`Error populating ${entityType}:`, error);
        setNotification({
          message: `Failed to populate ${entityType}: ${error?.message || "Unknown error"}`,
          variant: "error",
        });
        throw error;
      } finally {
        // Clear loading state
        setPopulatingStates((prev) => ({ ...prev, [entityType]: false }));
      }
    },
    [
      encounterReference,
      organizationReference,
      patientReference,
      queryClient,
      executeCallback,
      setNotification,
      clearAfterApply,
    ],
  );

  // Helper function to check if sexual history has data
  const hasSexualHistoryData = (sexual: any): boolean => {
    return hasNestedData(sexual);
  };
  const handleSmokingTypesBlur = useCallback(() => {
    // Only trigger blur behavior when no values are currently selected
    const currentTypes = editedContent.social?.smokeUse?.details?.type || [];
    if (currentTypes.length === 0 && inputValue.trim() !== "") {
      const isExisting = ["Cigarette", "Tobacco", "Marijuana", "Vape"].some(
        (option) => option.toLowerCase() === inputValue.toLowerCase(),
      );
      if (!isExisting) {
        // Add the new option to the selected values
        setEditedContent((prev) => ({
          ...prev,
          social: {
            ...(prev.social || {}),
            smokeUse: {
              ...(prev.social?.smokeUse || {}),
              details: {
                ...(prev.social?.smokeUse?.details || {}),
                type: [
                  ...(prev.social?.smokeUse?.details?.type || []),
                  inputValue,
                ],
              },
            },
          },
        }));
        setInputValue("");
      }
    }
  }, [inputValue, editedContent.social?.smokeUse?.details?.type]);

  const handleEdit = (section: string) => {
    setEditingSections((prev) => ({ ...prev, [section]: true }));
  };

  const handleContraceptionMethodsBlur = useCallback(() => {
    // Only trigger blur behavior when no values are currently selected
    const currentMethods = editedContent.sexual?.contraception?.methods || [];
    if (currentMethods.length === 0 && contraceptionInputValue.trim() !== "") {
      const isExisting = contraceptionMethods.some(
        (option) =>
          option.display.toLowerCase() ===
          contraceptionInputValue.toLowerCase(),
      );
      if (!isExisting) {
        // Add the new option to the selected values
        setEditedContent((prev) => ({
          ...prev,
          sexual: {
            ...(prev.sexual || {}),
            contraception: {
              ...(prev.sexual?.contraception || {}),
              methods: [
                ...(prev.sexual?.contraception?.methods || []),
                contraceptionInputValue,
              ],
            },
          },
        }));
        setContraceptionInputValue("");
      }
    }
  }, [contraceptionInputValue, editedContent.sexual?.contraception?.methods]);

  if (!history) return null;

  const handleCancel = (section: string) => {
    setEditingSections((prev) => ({ ...prev, [section]: false }));
    setEditedContent(history);
  };

  const handleSave = async (section: string) => {
    if (!copilotData?.id) return;

    try {
      setIsSaving(true);

      const humanEditedSection =
        resolveHumanEditedSectionFromHistorySubsection(section);
      const notePayload: TCopilot = {
        ...copilotData,
        note: {
          ...copilotData.note,
          history: {
            ...copilotData.note.history,
            [section]: editedContent[section],
          },
        },
      };
      const updatedNote: TCopilot = humanEditedSection
        ? withSectionHumanEditedExtension(notePayload, humanEditedSection)
        : notePayload;

      const response = await medicalsApi.updateCopilotNote(
        copilotData.id,
        updatedNote,
        encounterHeaders,
      );

      setCopilotData(response.data);
      setNotification({
        message: `${startCase(section)} updated successfully`,
        variant: "success",
      });
      setEditingSections((prev) => ({ ...prev, [section]: false }));
    } catch (error) {
      console.error(error);
      setNotification({
        message: `Failed to update ${section}`,
        variant: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box
      component="div"
      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
    >
      {/* Medical History */}
      <div>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="subtitle1" fontWeight="bold">
            Medical History
          </Typography>
          <CopilotSectionEditedBadge section="medical-history" />
          {!editingSections.medical && !readOnly && (
            <IconButton size="small" onClick={() => handleEdit("medical")}>
              <Edit fontSize="small" />
            </IconButton>
          )}
          {!readOnly &&
            hasLinkedEncounter &&
            !editingSections.medical &&
            hasMedicalHistoryData(editedContent.medical) &&
            shouldShowHistoryAccept("medical history") && (
              <Button
                startIcon={<AutoAwesome />}
                size="small"
                variant="outlined"
                sx={{ ml: "auto" }}
                disabled={populatingStates["medical history"]}
                onClick={() => {
                  setPopulatingStates((prev) => ({
                    ...prev,
                    "medical history": true,
                  }));
                  handlePopulate(
                    () =>
                      CopilotManager.populateMedicalHistory(
                        encounterReference?.identifier,
                        organizationReference?.identifier,
                        countryCode,
                      ),
                    "medical history",
                    [],
                    "copilot_refresh_history",
                  );
                }}
              >
                {populatingStates["medical history"]
                  ? "Accepting..."
                  : "Accept"}
              </Button>
            )}
        </Box>
        {editingSections.medical ? (
          <Box sx={{ mt: 1 }}>
            {editedContent.medical?.map((item: string, index: number) => (
              <Box key={index} sx={{ display: "flex", gap: 1, mb: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  value={item}
                  onChange={(e) => {
                    const newMedical = [...editedContent.medical];
                    newMedical[index] = e.target.value;
                    setEditedContent((prev) => ({
                      ...prev,
                      medical: newMedical,
                    }));
                  }}
                />
                <IconButton
                  size="small"
                  onClick={() => {
                    const newMedical = editedContent.medical.filter(
                      (_: any, i: number) => i !== index,
                    );
                    setEditedContent((prev) => ({
                      ...prev,
                      medical: newMedical,
                    }));
                  }}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Box>
            ))}
            <Button
              size="small"
              onClick={() => {
                setEditedContent((prev) => ({
                  ...prev,
                  medical: [...(prev.medical || []), ""],
                }));
              }}
            >
              Add Item
            </Button>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1,
                mt: 1,
              }}
            >
              <Button
                size="small"
                onClick={() => handleCancel("medical")}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <LoadingButton
                size="small"
                variant="contained"
                onClick={() => handleSave("medical")}
                loading={isSaving}
              >
                Save
              </LoadingButton>
            </Box>
          </Box>
        ) : (
          <div>
            {history.medical && history.medical.length > 0 ? (
              <ul style={{ margin: 0, paddingLeft: "1.5rem" }}>
                {history.medical?.map((item, index) => (
                  <li key={index}>
                    <Typography variant="body2">{item}</Typography>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        )}
      </div>

      {/* Family History */}
      {/* {history.family && history.family.length > 0 && ( */}
      <div>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="subtitle1" fontWeight="bold">
            Family History
          </Typography>
          <CopilotSectionEditedBadge section="family-history" />
          {!editingSections?.family && !readOnly && (
            <IconButton size="small" onClick={() => handleEdit("family")}>
              <Edit fontSize="small" />
            </IconButton>
          )}
          {!readOnly &&
            hasLinkedEncounter &&
            !editingSections.family &&
            hasFamilyHistoryData(editedContent.family) &&
            shouldShowHistoryAccept("family history") && (
              <Button
                startIcon={<AutoAwesome />}
                size="small"
                variant="outlined"
                sx={{ ml: "auto" }}
                disabled={populatingStates["family history"]}
                onClick={() => {
                  setPopulatingStates((prev) => ({
                    ...prev,
                    "family history": true,
                  }));
                  handlePopulate(
                    () =>
                      CopilotManager.populateFamilyHistory(
                        encounterReference?.identifier,
                        organizationReference?.identifier,
                        countryCode,
                      ),
                    "family history",
                    [],
                    "copilot_refresh_history",
                  );
                }}
              >
                {populatingStates["family history"] ? "Accepting..." : "Accept"}
              </Button>
            )}
        </Box>
        {editingSections?.family ? (
          <Box sx={{ mt: 1 }}>
            {editedContent.family?.map(
              (item: FamilyHistoryItem, index: number) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    mb: 2,
                  }}
                >
                  <TextField
                    fullWidth
                    size="small"
                    label="Condition"
                    value={item.condition || ""}
                    onChange={(e) => {
                      const newFamily = [...editedContent.family];
                      newFamily[index] = {
                        ...newFamily[index],
                        condition: e.target.value,
                        members: newFamily[index]?.members || [],
                      };
                      setEditedContent((prev) => ({
                        ...prev,
                        family: newFamily,
                      }));
                    }}
                  />
                  {item.members?.map((member, memberIndex) => (
                    <Box
                      key={memberIndex}
                      sx={{ display: "flex", gap: 1, ml: 2 }}
                    >
                      <TextField
                        size="small"
                        label="Relationship"
                        value={member.relationShip || ""}
                        onChange={(e) => {
                          const newFamily = [...editedContent.family];
                          newFamily[index].members[memberIndex] = {
                            ...newFamily[index].members[memberIndex],
                            relationShip: e.target.value,
                          };
                          setEditedContent((prev) => ({
                            ...prev,
                            family: newFamily,
                          }));
                        }}
                      />
                      <TextField
                        size="small"
                        label="Age"
                        value={member.age || ""}
                        onChange={(e) => {
                          const newFamily = [...editedContent.family];
                          newFamily[index].members[memberIndex] = {
                            ...newFamily[index].members[memberIndex],
                            age: e.target.value
                              ? parseInt(e.target.value)
                              : null,
                          };
                          setEditedContent((prev) => ({
                            ...prev,
                            family: newFamily,
                          }));
                        }}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={member.deceased || false}
                            onChange={(e) => {
                              const newFamily = [...editedContent.family];
                              newFamily[index].members[memberIndex] = {
                                ...newFamily[index].members[memberIndex],
                                deceased: e.target.checked,
                              };
                              setEditedContent((prev) => ({
                                ...prev,
                                family: newFamily,
                              }));
                            }}
                          />
                        }
                        label="Deceased"
                      />
                      <IconButton
                        size="small"
                        onClick={() => {
                          const newFamily = [...editedContent.family];
                          newFamily[index].members = newFamily[
                            index
                          ].members.filter((_, i) => i !== memberIndex);
                          setEditedContent((prev) => ({
                            ...prev,
                            family: newFamily,
                          }));
                        }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                  <Box sx={{ display: "flex", gap: 1, ml: 2 }}>
                    <Button
                      size="small"
                      onClick={() => {
                        const newFamily = [...editedContent.family];
                        if (!newFamily[index].members) {
                          newFamily[index].members = [];
                        }
                        newFamily[index].members.push({
                          relationShip: "",
                          age: null,
                          deceased: false,
                        });
                        setEditedContent((prev) => ({
                          ...prev,
                          family: newFamily,
                        }));
                      }}
                    >
                      Add Family Member
                    </Button>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => {
                      const newFamily = editedContent.family.filter(
                        (_, i) => i !== index,
                      );
                      setEditedContent((prev) => ({
                        ...prev,
                        family: newFamily,
                      }));
                    }}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>
              ),
            )}
            <Button
              size="small"
              onClick={() => {
                setEditedContent((prev) => ({
                  ...prev,
                  family: [
                    ...(prev.family || []),
                    { condition: "", members: [] },
                  ],
                }));
              }}
            >
              Add Condition
            </Button>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1,
                mt: 1,
              }}
            >
              <Button
                size="small"
                onClick={() => handleCancel("family")}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <LoadingButton
                size="small"
                variant="contained"
                onClick={() => handleSave("family")}
                loading={isSaving}
              >
                Save
              </LoadingButton>
            </Box>
          </Box>
        ) : (
          <div>
            {history.family && history.family.length > 0 ? (
              <ul style={{ margin: 0, paddingLeft: "1.5rem" }}>
                {history.family?.map((item: any, index: number) => (
                  <li key={index}>
                    <Typography variant="body2" component="div">
                      <strong>{item.condition}</strong>
                      {item.members && item.members.length > 0 && (
                        <ul
                          style={{
                            marginTop: "0.25rem",
                            marginBottom: "0.5rem",
                          }}
                        >
                          {item.members?.map((member, memberIndex) => (
                            <li key={memberIndex}>
                              {member.relationShip}
                              {member.age && ` (${member.age} years)`}
                              {member.deceased && " - Deceased"}
                            </li>
                          ))}
                        </ul>
                      )}
                    </Typography>{" "}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        )}
      </div>
      {/* )} */}

      {/* Hospitalizations */}
      {/* {history?.hospitalizations?.length > 0 && ( */}
      <div>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="subtitle1" fontWeight="bold">
            Hospitalizations
          </Typography>
          <CopilotSectionEditedBadge section="hospitalizations" />
          {!editingSections?.hospitalizations && !readOnly && (
            <IconButton
              size="small"
              onClick={() => handleEdit("hospitalizations")}
            >
              <Edit fontSize="small" />
            </IconButton>
          )}
          {!readOnly &&
            hasLinkedEncounter &&
            !editingSections.hospitalizations &&
            hasNestedData(editedContent.hospitalizations) &&
            shouldShowHistoryAccept("hospitalizations") && (
              <Button
                startIcon={<AutoAwesome />}
                size="small"
                variant="outlined"
                sx={{ ml: "auto" }}
                disabled={populatingStates["hospitalizations"]}
                onClick={() => {
                  setPopulatingStates((prev) => ({
                    ...prev,
                    hospitalizations: true,
                  }));
                  handlePopulate(
                    () =>
                      CopilotManager.populateHospitalization(
                        encounterReference?.identifier,
                        organizationReference?.identifier,
                        countryCode,
                      ),
                    "hospitalizations",
                    [],
                    "copilot_refresh_history",
                  );
                }}
              >
                {populatingStates["hospitalizations"]
                  ? "Accepting..."
                  : "Accept"}
              </Button>
            )}
        </Box>
        {editingSections?.hospitalizations ? (
          <Box sx={{ mt: 1 }}>
            {editedContent.hospitalizations?.map(
              (hospitalization: string, index: number) => (
                <Box key={index} sx={{ display: "flex", gap: 1, mb: 1 }}>
                  <TextField
                    fullWidth
                    size="small"
                    value={hospitalization}
                    onChange={(e) => {
                      const newHospitalizations = [
                        ...editedContent.hospitalizations,
                      ];
                      newHospitalizations[index] = e.target.value;
                      setEditedContent((prev) => ({
                        ...prev,
                        hospitalizations: newHospitalizations,
                      }));
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => {
                      const newHospitalizations =
                        editedContent.hospitalizations.filter(
                          (_: any, i: number) => i !== index,
                        );
                      setEditedContent((prev) => ({
                        ...prev,
                        hospitalizations: newHospitalizations,
                      }));
                    }}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>
              ),
            )}
            <Button
              size="small"
              onClick={() => {
                setEditedContent((prev) => ({
                  ...prev,
                  hospitalizations: [...(prev.hospitalizations || []), ""],
                }));
              }}
            >
              Add Item
            </Button>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1,
                mt: 1,
              }}
            >
              <Button
                size="small"
                onClick={() => handleCancel("hospitalizations")}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <LoadingButton
                size="small"
                variant="contained"
                onClick={() => handleSave("hospitalizations")}
                loading={isSaving}
              >
                Save
              </LoadingButton>
            </Box>
          </Box>
        ) : (
          <div>
            {history.hospitalizations && history.hospitalizations.length > 0 ? (
              <ul style={{ margin: 0, paddingLeft: "1.5rem" }}>
                {history.hospitalizations?.map((hospitalization, index) => (
                  <li key={index}>
                    <Typography variant="body2">{hospitalization}</Typography>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        )}
      </div>
      {/* Past Surgery */}
      {/* {history?.procedures?.length > 0 && ( */}
      <div>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="subtitle1" fontWeight="bold">
            Past Surgery
          </Typography>
          <CopilotSectionEditedBadge section="procedures" />
          {!editingSections?.procedures && !readOnly && (
            <IconButton size="small" onClick={() => handleEdit("procedures")}>
              <Edit fontSize="small" />
            </IconButton>
          )}
          {!readOnly &&
            hasLinkedEncounter &&
            !editingSections.procedures &&
            hasProcedureData(editedContent.procedures) &&
            shouldShowHistoryAccept("procedures") && (
              <Button
                startIcon={<AutoAwesome />}
                size="small"
                variant="outlined"
                sx={{ ml: "auto" }}
                disabled={populatingStates["procedures"]}
                onClick={() => {
                  setPopulatingStates((prev) => ({
                    ...prev,
                    procedures: true,
                  }));
                  handlePopulate(
                    () =>
                      CopilotManager.populateProcedures(
                        encounterReference?.identifier,
                        organizationReference?.identifier,
                        countryCode,
                      ),
                    "procedures",
                    [],
                    "copilot_refresh_procedures",
                  );
                }}
              >
                {populatingStates["procedures"] ? "Accepting..." : "Accept"}
              </Button>
            )}
        </Box>
        {editingSections?.procedures ? (
          <Box sx={{ mt: 1 }}>
            {editedContent.procedures?.map(
              (procedure: string, index: number) => (
                <Box key={index} sx={{ display: "flex", gap: 1, mb: 1 }}>
                  <TextField
                    fullWidth
                    size="small"
                    value={procedure}
                    onChange={(e) => {
                      const newProcedures = [...editedContent.procedures];
                      newProcedures[index] = e.target.value;
                      setEditedContent((prev) => ({
                        ...prev,
                        procedures: newProcedures,
                      }));
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => {
                      const newProcedures = editedContent.procedures.filter(
                        (_: any, i: number) => i !== index,
                      );
                      setEditedContent((prev) => ({
                        ...prev,
                        procedures: newProcedures,
                      }));
                    }}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>
              ),
            )}
            <Button
              size="small"
              onClick={() => {
                setEditedContent((prev) => ({
                  ...prev,
                  procedures: [...(prev.procedures || []), ""],
                }));
              }}
            >
              Add Item
            </Button>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1,
                mt: 1,
              }}
            >
              <Button
                size="small"
                onClick={() => handleCancel("procedures")}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <LoadingButton
                size="small"
                variant="contained"
                onClick={() => handleSave("procedures")}
                loading={isSaving}
              >
                Save
              </LoadingButton>
            </Box>
          </Box>
        ) : (
          <div>
            {history.procedures && history.procedures.length > 0 ? (
              <ul style={{ margin: 0, paddingLeft: "1.5rem" }}>
                {history.procedures?.map((procedure, index) => (
                  <li key={index}>
                    <Typography variant="body2">{procedure}</Typography>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        )}
      </div>
      {/* )} */}

      {/* Social History */}
      <div>
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          justifyContent="space-between"
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="subtitle1" fontWeight="bold">
              Social History
            </Typography>
            <CopilotSectionEditedBadge section="social-history" />
            {!editingSections.social && !readOnly && (
              <IconButton size="small" onClick={() => handleEdit("social")}>
                <Edit fontSize="small" />
              </IconButton>
            )}
          </Box>
          {!readOnly &&
            hasLinkedEncounter &&
            !editingSections.social &&
            hasSocialHistoryData(editedContent.social) &&
            shouldShowHistoryAccept("social history") && (
              <Button
                startIcon={<AutoAwesome />}
                size="small"
                variant="outlined"
                sx={{ ml: "auto" }}
                disabled={populatingStates["social history"]}
                onClick={() => {
                  setPopulatingStates((prev) => ({
                    ...prev,
                    "social history": true,
                  }));
                  handlePopulate(
                    () =>
                      CopilotManager.populateSocialHistory(
                        editedContent.social,
                        patientReference,
                        encounterReference,
                        practitionerReference,
                        copilotData?.id,
                      ),
                    "social history",
                    [],
                    "copilot_refresh_history",
                  );
                }}
              >
                {populatingStates["social history"] ? "Accepting..." : "Accept"}
              </Button>
            )}
        </Box>
        {editingSections?.social ? (
          <Box sx={{ mt: 1 }}>
            <Grid container spacing={2}>
              {/* Occupation */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Occupation"
                  value={editedContent.social?.occupation?.current}
                  onChange={(e) =>
                    setEditedContent((prev) => ({
                      ...prev,
                      social: {
                        ...(prev.social || {}),
                        occupation: {
                          ...(prev.social?.occupation || {}),
                          current: e.target.value,
                        },
                      },
                    }))
                  }
                />
              </Grid>

              {/* Living Situation */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Living Situation"
                  value={
                    editedContent.social?.livingSituation?.accommodationType
                  }
                  onChange={(e) =>
                    setEditedContent((prev) => ({
                      ...prev,
                      social: {
                        ...(prev.social || {}),
                        livingSituation: {
                          ...(prev.social?.livingSituation || {}),
                          accommodationType: e.target.value,
                        },
                      },
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Household Members"
                  type="number"
                  value={
                    editedContent.social?.livingSituation?.householdMembers
                  }
                  onChange={(e) =>
                    setEditedContent((prev) => ({
                      ...prev,
                      social: {
                        ...(prev.social || {}),
                        livingSituation: {
                          ...(prev.social?.livingSituation || {}),
                          householdMembers: parseInt(e.target.value),
                        },
                      },
                    }))
                  }
                />
              </Grid>

              {/* Smoking */}
              <Grid item xs={12}>
                <Typography variant="subtitle2">Smoking</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  select
                  label="Smoking Status"
                  value={editedContent.social?.smokeUse?.status}
                  onChange={(e) =>
                    setEditedContent((prev) => ({
                      ...prev,
                      social: {
                        ...(prev.social || {}),
                        smokeUse: {
                          ...(prev.social?.smokeUse || {}),
                          status: e.target.value as
                            | "active"
                            | "passive"
                            | "quit"
                            | "never",
                        },
                      },
                    }))
                  }
                >
                  {["active", "passive", "quit", "never"].map((status) => (
                    <MenuItem key={status} value={status}>
                      {startCase(status)}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  fullWidth
                  size="small"
                  multiple
                  forcePopupIcon
                  options={["Cigarette", "Tobacco", "Marijuana", "Vape"]}
                  value={editedContent.social?.smokeUse?.details?.type}
                  inputValue={inputValue}
                  onInputChange={(event, newInputValue) => {
                    setInputValue(newInputValue);
                  }}
                  onChange={(e, value) =>
                    setEditedContent((prev) => ({
                      ...prev,
                      social: {
                        ...(prev.social || {}),
                        smokeUse: {
                          ...(prev.social?.smokeUse || {}),
                          details: {
                            ...(prev.social?.smokeUse?.details || {}),
                            type: value,
                          },
                        },
                      },
                    }))
                  }
                  filterOptions={(options, params) => {
                    const filter = createFilterOptions<string>();
                    const filtered = filter(options, params);
                    const { inputValue } = params;
                    const isExisting = options.some(
                      (option) => option === inputValue,
                    );
                    if (inputValue !== "" && !isExisting) {
                      filtered.push(`Add ${inputValue}`);
                    }
                    return filtered;
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      label="Smoking Types"
                      onBlur={handleSmokingTypesBlur}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Packs per day"
                  value={editedContent.social?.smokeUse?.details?.packsPerDay}
                  onChange={(e) =>
                    setEditedContent((prev) => {
                      const updatedContent = {
                        ...prev,
                        social: {
                          ...(prev.social || {}),
                          smokeUse: {
                            ...(prev.social?.smokeUse || {}),
                            details: {
                              ...(prev.social?.smokeUse?.details || {}),
                              packsPerDay: parseInt(e.target.value),
                            },
                          },
                        },
                      };
                      return updatedContent as TCopilotPatientHistory;
                    })
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Years smoked"
                  value={editedContent.social?.smokeUse?.details?.yearsSmoked}
                  onChange={(e) =>
                    setEditedContent((prev) => {
                      const updatedContent = {
                        ...prev,
                        social: {
                          ...(prev.social || {}),
                          smokeUse: {
                            ...(prev.social?.smokeUse || {}),
                            details: {
                              ...(prev.social?.smokeUse?.details || {}),
                              yearsSmoked: parseInt(e.target.value),
                            },
                          },
                        },
                      };
                      return updatedContent as TCopilotPatientHistory;
                    })
                  }
                />
              </Grid>

              {/* Alcohol Use */}
              <Grid item xs={12}>
                <Typography variant="subtitle2">Alcohol Use</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="Alcohol Consumption"
                  value={
                    editedContent.social?.alcoholUse?.status ? "yes" : "no"
                  }
                  onChange={(e) =>
                    setEditedContent((prev) => ({
                      ...prev,
                      social: {
                        ...(prev.social || {}),
                        alcoholUse: {
                          ...(prev.social?.alcoholUse || {}),
                          status: e.target.value === "yes",
                        },
                      },
                    }))
                  }
                >
                  <MenuItem value="yes">Yes</MenuItem>
                  <MenuItem value="no">No</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  multiple
                  options={["Beer", "Wine", "Spirits"]}
                  value={editedContent.social?.alcoholUse?.details?.types || []}
                  onChange={(event, newValue) => {
                    setEditedContent((prev) => ({
                      ...prev,
                      social: {
                        ...(prev.social || {}),
                        alcoholUse: {
                          ...(prev.social?.alcoholUse || {}),
                          details: {
                            ...(prev.social?.alcoholUse?.details || {}),
                            types: newValue?.map((v) => v.toLowerCase()),
                          },
                        },
                      },
                    }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Alcohol Types"
                      placeholder="Select types"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Drinks"
                  type="number"
                  value={
                    editedContent.social?.alcoholUse?.details?.drinksPerPeriod
                  }
                  onChange={(e) =>
                    setEditedContent((prev) => ({
                      ...prev,
                      social: {
                        ...(prev.social || {}),
                        alcoholUse: {
                          ...(prev.social?.alcoholUse || {}),
                          details: {
                            ...(prev.social?.alcoholUse?.details || {}),
                            drinksPerPeriod: parseInt(e.target.value),
                          },
                        },
                      },
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Per Frequency"
                  value={editedContent.social?.alcoholUse?.details?.period}
                  onChange={(e) =>
                    setEditedContent((prev) => ({
                      ...prev,
                      social: {
                        ...(prev.social || {}),
                        alcoholUse: {
                          ...(prev.social?.alcoholUse || {}),
                          details: {
                            ...(prev.social?.alcoholUse?.details || {}),
                            period: e.target.value,
                          },
                        },
                      },
                    }))
                  }
                />
              </Grid>

              {/* Exercise */}
              <Grid item xs={12}>
                <Typography variant="subtitle2">Exercise</Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  select
                  size="small"
                  label="Exercise Frequency"
                  value={editedContent?.social?.exercise?.frequency}
                  onChange={(e) =>
                    setEditedContent((prev) => ({
                      ...prev,
                      social: {
                        ...(prev.social || {}),
                        exercise: {
                          ...(prev.social?.exercise || {}),
                          frequency: e.target.value,
                        },
                      },
                    }))
                  }
                >
                  {["Sedentary/None", "2-3 Times/Week", "5-6 Times/Week"]?.map(
                    (freq) => (
                      <MenuItem key={freq} value={freq}>
                        {freq}
                      </MenuItem>
                    ),
                  )}
                </TextField>
              </Grid>
            </Grid>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1,
                mt: 1,
              }}
            >
              <Button
                size="small"
                onClick={() => handleCancel("social")}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <LoadingButton
                size="small"
                variant="contained"
                onClick={() => handleSave("social")}
                loading={isSaving}
              >
                Save
              </LoadingButton>
            </Box>
          </Box>
        ) : (
          <div>
            {history.social &&
            Object.entries(history.social).filter(
              ([_, value]) => value !== null,
            ).length > 0 ? (
              <>
                {Object.entries(history.social)
                  .filter(([_, value]) => value !== null)
                  ?.map(([key, value]) => (
                    <Box key={key} sx={{ mt: 0.5 }}>
                      <Typography
                        variant="subtitle2"
                        component="span"
                        fontWeight="bold"
                      >
                        {startCase(key)}:
                      </Typography>
                      <Typography
                        variant="body2"
                        component="span"
                        sx={{ ml: 1 }}
                      >
                        {formatValue(value)}
                      </Typography>
                    </Box>
                  ))}
              </>
            ) : null}
          </div>
        )}
      </div>

      {/* Sexual History */}
      <div>
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          justifyContent="space-between"
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="subtitle1" fontWeight="bold">
              Sexual History
            </Typography>
            <CopilotSectionEditedBadge section="sexual-history" />
            {!editingSections.sexual && !readOnly && (
              <IconButton size="small" onClick={() => handleEdit("sexual")}>
                <Edit fontSize="small" />
              </IconButton>
            )}
          </Box>
          {!readOnly &&
            hasLinkedEncounter &&
            !editingSections.sexual &&
            hasSexualHistoryData(editedContent.sexual) &&
            shouldShowHistoryAccept("sexual history") && (
              <Button
                startIcon={<AutoAwesome />}
                size="small"
                variant="outlined"
                sx={{ ml: "auto" }}
                disabled={populatingStates["sexual history"]}
                onClick={() => {
                  setPopulatingStates((prev) => ({
                    ...prev,
                    "sexual history": true,
                  }));
                  handlePopulate(
                    () =>
                      CopilotManager.populateSexualHistory(
                        editedContent.sexual,
                        patientReference,
                        encounterReference,
                        practitionerReference,
                        copilotData?.id,
                      ),
                    "sexual history",
                    [],
                    "copilot_refresh_history",
                  );
                }}
              >
                {populatingStates["sexual history"] ? "Accepting..." : "Accept"}
              </Button>
            )}
        </Box>
        {editingSections.sexual ? (
          <Box sx={{ mt: 1 }}>
            <Grid container spacing={2}>
              {/* Sexual Activity */}
              <Grid item xs={12}>
                <Typography variant="subtitle2">Sexual Activity</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl component="fieldset">
                  <FormLabel component="legend">Ever Sexually Active</FormLabel>
                  <RadioGroup
                    row
                    value={
                      editedContent.sexual?.activity?.everActive ? "yes" : "no"
                    }
                    onChange={(e) =>
                      setEditedContent((prev) => ({
                        ...prev,
                        sexual: {
                          ...(prev.sexual || {}),
                          activity: {
                            ...(prev.sexual?.activity || {}),
                            everActive: e.target.value === "yes",
                          },
                        },
                      }))
                    }
                  >
                    <FormControlLabel
                      value="yes"
                      control={<Radio />}
                      label="Yes"
                    />
                    <FormControlLabel
                      value="no"
                      control={<Radio />}
                      label="No"
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl component="fieldset">
                  <FormLabel component="legend">
                    Currently Sexually Active
                  </FormLabel>
                  <RadioGroup
                    row
                    value={
                      editedContent.sexual?.activity?.currentlyActive
                        ? "yes"
                        : "no"
                    }
                    onChange={(e) =>
                      setEditedContent((prev) => ({
                        ...prev,
                        sexual: {
                          ...(prev.sexual || {}),
                          activity: {
                            ...(prev.sexual?.activity || {}),
                            currentlyActive: e.target.value === "yes",
                          },
                        },
                      }))
                    }
                  >
                    <FormControlLabel
                      value="yes"
                      control={<Radio />}
                      label="Yes"
                    />
                    <FormControlLabel
                      value="no"
                      control={<Radio />}
                      label="No"
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  size="small"
                  label="Partner Gender"
                  value={editedContent.sexual?.activity?.partnerGender || ""}
                  onChange={(e) =>
                    setEditedContent((prev) => ({
                      ...prev,
                      sexual: {
                        ...(prev.sexual || {}),
                        activity: {
                          ...(prev.sexual?.activity || {}),
                          partnerGender: e.target.value as
                            | "Male"
                            | "Female"
                            | "Both"
                            | null,
                        },
                      },
                    }))
                  }
                >
                  {["Male", "Female", "Both"]?.map((gender) => (
                    <MenuItem key={gender} value={gender}>
                      {gender}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  multiple
                  fullWidth
                  size="small"
                  options={["Vaginal", "Anal", "Oral"]}
                  value={editedContent.sexual?.activity?.activityTypes || []}
                  onChange={(_, newValue) => {
                    setEditedContent((prev) => ({
                      ...prev,
                      sexual: {
                        ...(prev.sexual || {}),
                        activity: {
                          ...(prev.sexual?.activity || {}),
                          activityTypes: newValue as (
                            | "Vaginal"
                            | "Anal"
                            | "Oral"
                          )[],
                        },
                      },
                    }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      label="Activity Types"
                    />
                  )}
                />
              </Grid>

              {/* Contraception */}
              <Grid item xs={12}>
                <Typography variant="subtitle2">Contraception</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl component="fieldset">
                  <FormLabel component="legend">Uses Contraception</FormLabel>
                  <RadioGroup
                    row
                    value={
                      editedContent.sexual?.contraception?.usesContraception
                        ? "yes"
                        : "no"
                    }
                    onChange={(e) =>
                      setEditedContent((prev) => ({
                        ...prev,
                        sexual: {
                          ...(prev.sexual || {}),
                          contraception: {
                            ...(prev.sexual?.contraception || {}),
                            usesContraception: e.target.value === "yes",
                          },
                        },
                      }))
                    }
                  >
                    <FormControlLabel
                      value="yes"
                      control={<Radio />}
                      label="Yes"
                    />
                    <FormControlLabel
                      value="no"
                      control={<Radio />}
                      label="No"
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl component="fieldset">
                  <FormLabel component="legend">Interested in Change</FormLabel>
                  <RadioGroup
                    row
                    value={
                      editedContent.sexual?.contraception?.interestedInChange
                        ? "yes"
                        : "no"
                    }
                    onChange={(e) =>
                      setEditedContent((prev) => ({
                        ...prev,
                        sexual: {
                          ...(prev.sexual || {}),
                          contraception: {
                            ...(prev.sexual?.contraception || {}),
                            interestedInChange: e.target.value === "yes",
                          },
                        },
                      }))
                    }
                  >
                    <FormControlLabel
                      value="yes"
                      control={<Radio />}
                      label="Yes"
                    />
                    <FormControlLabel
                      value="no"
                      control={<Radio />}
                      label="No"
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl component="fieldset" fullWidth>
                  <Autocomplete
                    multiple
                    forcePopupIcon
                    freeSolo
                    inputValue={contraceptionInputValue}
                    options={contraceptionMethods}
                    value={
                      editedContent.sexual?.contraception?.methods?.map(
                        (method) => ({
                          code: method,
                          display: method,
                        }),
                      ) || []
                    }
                    getOptionLabel={(option) => {
                      if (typeof option === "string") return option;
                      return option.display;
                    }}
                    isOptionEqualToValue={(option, value) =>
                      option.code === value.code
                    }
                    onInputChange={(event, newInputValue) => {
                      setContraceptionInputValue(newInputValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        label="Contraception Methods"
                        onBlur={handleContraceptionMethodsBlur}
                      />
                    )}
                    filterOptions={(options, params) => {
                      const filter = createFilterOptions<{
                        code: string;
                        display: string;
                      }>();
                      const filtered = filter(options, params);
                      const { inputValue } = params;
                      const isExisting = options.some(
                        (option) => option.display === inputValue,
                      );
                      if (inputValue !== "" && !isExisting) {
                        filtered.push({
                          display: `Add "${inputValue}"`,
                          code: inputValue,
                        });
                      }
                      return filtered;
                    }}
                    onChange={(_, newValue) => {
                      const methods = newValue?.map((v) =>
                        typeof v === "string" ? v : v.display,
                      );
                      setEditedContent((prev) => ({
                        ...prev,
                        sexual: {
                          ...(prev.sexual || {}),
                          contraception: {
                            ...(prev.sexual?.contraception || {}),
                            methods,
                          },
                        },
                      }));
                    }}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  select
                  size="small"
                  label="Satisfaction with Current Method"
                  value={
                    editedContent.sexual?.contraception?.satisfaction || ""
                  }
                  onChange={(e) =>
                    setEditedContent((prev) => ({
                      ...prev,
                      sexual: {
                        ...(prev.sexual || {}),
                        contraception: {
                          ...(prev.sexual?.contraception || {}),
                          satisfaction: e.target.value as
                            | "satisfied"
                            | "unsatisfied"
                            | "somewhat",
                        },
                      },
                    }))
                  }
                >
                  {["satisfied", "unsatisfied", "somewhat"]?.map((status) => (
                    <MenuItem key={status} value={status}>
                      {startCase(status)}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* Condom Use */}
              <Grid item xs={12}>
                <Typography variant="subtitle2">Condom Use</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  fullWidth
                  size="small"
                  options={["never", "sometimes", "always"]}
                  getOptionLabel={(option) => startCase(option)}
                  value={editedContent.sexual?.condomUse?.lastYear || null}
                  onChange={(_, newValue) =>
                    setEditedContent((prev) => ({
                      ...prev,
                      sexual: {
                        ...(prev.sexual || {}),
                        condomUse: {
                          ...(prev.sexual?.condomUse || {}),
                          lastYear: newValue as
                            | "never"
                            | "sometimes"
                            | "always"
                            | null,
                        },
                      },
                    }))
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Condom Use in Last Year" />
                  )}
                  clearOnBlur
                  handleHomeEndKeys
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  size="small"
                  label="Condom Type"
                  value={editedContent.sexual?.condomUse?.type || ""}
                  onChange={(e) =>
                    setEditedContent((prev) => ({
                      ...prev,
                      sexual: {
                        ...(prev.sexual || {}),
                        condomUse: {
                          ...(prev.sexual?.condomUse || {}),
                          type: e.target.value as "Male" | "Female" | "Both",
                        },
                      },
                    }))
                  }
                >
                  {["Male", "Female", "Both"]?.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* STIs */}
              <Grid item xs={12}>
                <Typography variant="subtitle2">STI History</Typography>
              </Grid>

              {/* Patient STI Status */}
              <Grid item xs={12}>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  Patient Status
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  size="small"
                  label="HIV Status"
                  value={editedContent.sexual?.stis?.hivStatus || ""}
                  onChange={(e) =>
                    setEditedContent((prev) => ({
                      ...prev,
                      sexual: {
                        ...(prev.sexual || {}),
                        stis: {
                          ...(prev.sexual?.stis || {}),
                          hivStatus: e.target.value as
                            | "positive"
                            | "negative"
                            | "unknown",
                        },
                      },
                    }))
                  }
                >
                  {["positive", "negative", "unknown"]?.map((status) => (
                    <MenuItem key={status} value={status}>
                      {startCase(status)}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl component="fieldset">
                  <FormLabel component="legend">
                    Previously Treated for STI
                  </FormLabel>
                  <RadioGroup
                    row
                    value={
                      editedContent.sexual?.stis?.previouslyTreatment
                        ? "yes"
                        : "no"
                    }
                    onChange={(e) =>
                      setEditedContent((prev) => ({
                        ...prev,
                        sexual: {
                          ...(prev.sexual || {}),
                          stis: {
                            ...(prev.sexual?.stis || {}),
                            previouslyTreatment: e.target.value === "yes",
                          },
                        },
                      }))
                    }
                  >
                    <FormControlLabel
                      value="yes"
                      control={<Radio />}
                      label="Yes"
                    />
                    <FormControlLabel
                      value="no"
                      control={<Radio />}
                      label="No"
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>

              {/* Partner STI Status */}
              <Grid item xs={12}>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ mb: 1, mt: 1 }}
                >
                  Partner Status
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl component="fieldset">
                  <FormLabel component="legend">
                    Partner Has STI History
                  </FormLabel>
                  <RadioGroup
                    row
                    value={
                      editedContent.sexual?.stis?.partnerHasHistory
                        ? "yes"
                        : "no"
                    }
                    onChange={(e) =>
                      setEditedContent((prev) => ({
                        ...prev,
                        sexual: {
                          ...(prev.sexual || {}),
                          stis: {
                            ...(prev.sexual?.stis || {}),
                            partnerHasHistory: e.target.value === "yes",
                          },
                        },
                      }))
                    }
                  >
                    <FormControlLabel
                      value="yes"
                      control={<Radio />}
                      label="Yes"
                    />
                    <FormControlLabel
                      value="no"
                      control={<Radio />}
                      label="No"
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl component="fieldset">
                  <FormLabel component="legend">
                    Partner Has Been Tested
                  </FormLabel>
                  <RadioGroup
                    row
                    value={
                      editedContent.sexual?.stis?.partnerHasTested
                        ? "yes"
                        : "no"
                    }
                    onChange={(e) =>
                      setEditedContent((prev) => {
                        const updatedSexual = {
                          ...prev.sexual,
                          activity: prev.sexual?.activity || {
                            everActive: false,
                            currentlyActive: false,
                            partnerGender: null,
                            activityType: [],
                          },
                          stis: {
                            ...(prev.sexual?.stis || {}),
                            partnerHasTested: e.target.value === "yes",
                          },
                        };
                        return {
                          ...prev,
                          sexual: updatedSexual,
                        };
                      })
                    }
                  >
                    <FormControlLabel
                      value="yes"
                      control={<Radio />}
                      label="Yes"
                    />
                    <FormControlLabel
                      value="no"
                      control={<Radio />}
                      label="No"
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>
            </Grid>

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1,
                mt: 2,
              }}
            >
              <Button
                size="small"
                onClick={() => handleCancel("sexual")}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <LoadingButton
                size="small"
                variant="contained"
                onClick={() => handleSave("sexual")}
                loading={isSaving}
              >
                Save
              </LoadingButton>
            </Box>
          </Box>
        ) : (
          <Box sx={{ mt: 1 }}>
            {history.sexual &&
            (Object.values(history.sexual).some(
              (section) =>
                section &&
                typeof section === "object" &&
                Object.values(section).some((value) => value !== null),
            ) ||
              Object.values(history.sexual).some(
                (value) => value !== null && typeof value !== "object",
              )) ? (
              <>
                {/* Display Sexual Activity */}
                {history.sexual?.activity && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      Sexual Activity:
                    </Typography>
                    <Typography variant="body2">
                      {history.sexual.activity.everActive
                        ? "Has been sexually active"
                        : "Never sexually active"}
                      {history.sexual.activity.currentlyActive &&
                        ", currently active"}
                      {history.sexual.activity.partnerGender &&
                        `, partner gender: ${history.sexual.activity.partnerGender}`}
                      {history.sexual.activity.activityTypes?.length > 0 &&
                        `, activity types: ${history.sexual.activity.activityTypes.join(", ")}`}
                    </Typography>
                  </Box>
                )}

                {/* Display Contraception */}
                {history.sexual?.contraception && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      Contraception:
                    </Typography>
                    <Typography variant="body2">
                      {history.sexual.contraception.usesContraception
                        ? "Uses contraception"
                        : "Does not use contraception"}
                      {history.sexual.contraception.methods?.length > 0 &&
                        `, methods: ${history.sexual.contraception.methods.join(", ")}`}
                      {history.sexual.contraception.satisfaction &&
                        `, satisfaction: ${startCase(history.sexual.contraception.satisfaction)}`}
                      {history.sexual.contraception.interestedInChange &&
                        ", interested in changing methods"}
                    </Typography>
                  </Box>
                )}

                {/* Display Condom Use */}
                {history.sexual?.condomUse && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      Condom Use:
                    </Typography>
                    <Typography variant="body2">
                      {history.sexual.condomUse.lastYear &&
                        `Last year: ${startCase(history.sexual.condomUse.lastYear)}`}
                      {history.sexual.condomUse.type &&
                        `, type: ${history.sexual.condomUse.type}`}
                    </Typography>
                  </Box>
                )}

                {/* Display STI History */}
                {history.sexual?.stis && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      STI History:
                    </Typography>
                    <Typography variant="body2">
                      {history.sexual.stis.hivStatus &&
                        `HIV Status: ${startCase(history.sexual.stis.hivStatus)}`}
                      {history.sexual.stis.previouslyTreatment &&
                        ", has been previously treated for STI"}
                      {history.sexual.stis.partnerHasHistory &&
                        ", partner has STI history"}
                      {history.sexual.stis.partnerHasTested &&
                        ", partner has been tested for STI"}
                    </Typography>
                  </Box>
                )}
              </>
            ) : null}
          </Box>
        )}
      </div>

      {/* Medications */}
      {/* {history.medications && history.medications.length > 0 && ( */}
      <div>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="subtitle1" fontWeight="bold">
            Medications
          </Typography>
          <CopilotSectionEditedBadge section="medications" />
          {!editingSections?.medications && !readOnly && (
            <IconButton size="small" onClick={() => handleEdit("medications")}>
              <Edit fontSize="small" />
            </IconButton>
          )}
          {!readOnly &&
            hasLinkedEncounter &&
            !editingSections.medications &&
            hasMedicationData(editedContent.medications) &&
            shouldShowHistoryAccept("medications") && (
              <Button
                startIcon={<AutoAwesome />}
                size="small"
                variant="outlined"
                sx={{ ml: "auto" }}
                disabled={populatingStates["medications"]}
                onClick={() => {
                  setPopulatingStates((prev) => ({
                    ...prev,
                    medications: true,
                  }));
                  handlePopulate(
                    () =>
                      CopilotManager.populateMedications(
                        encounterReference?.identifier,
                        organizationReference?.identifier,
                        countryCode,
                      ),
                    "medications",
                    [QueryKeys.MEDICATION, QueryKeys.MEDICATION_STATEMENT],
                    "copilot_refresh_medications",
                  );
                }}
              >
                {populatingStates["medications"] ? "Accepting..." : "Accept"}
              </Button>
            )}
        </Box>
        {editingSections?.medications ? (
          <Box sx={{ mt: 1 }}>
            {editedContent.medications?.map((med: string, index: number) => (
              <Box key={index} sx={{ display: "flex", gap: 1, mb: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  value={med}
                  onChange={(e) => {
                    const newMedications = [...editedContent.medications];
                    newMedications[index] = e.target.value;
                    setEditedContent((prev) => ({
                      ...prev,
                      medications: newMedications,
                    }));
                  }}
                />
                <IconButton
                  size="small"
                  onClick={() => {
                    const newMedications = editedContent.medications.filter(
                      (_: any, i: number) => i !== index,
                    );
                    setEditedContent((prev) => ({
                      ...prev,
                      medications: newMedications,
                    }));
                  }}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Box>
            ))}
            <Button
              size="small"
              onClick={() => {
                setEditedContent((prev) => ({
                  ...prev,
                  medications: [...(prev.medications || []), ""],
                }));
              }}
            >
              Add Item
            </Button>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1,
                mt: 1,
              }}
            >
              <Button
                size="small"
                onClick={() => handleCancel("medications")}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <LoadingButton
                size="small"
                variant="contained"
                onClick={() => handleSave("medications")}
                loading={isSaving}
              >
                Save
              </LoadingButton>
            </Box>
          </Box>
        ) : (
          <div>
            {history.medications && history.medications.length > 0 ? (
              <ul style={{ margin: 0, paddingLeft: "1.5rem" }}>
                {history.medications?.map((med, index) => (
                  <li key={index}>
                    <Typography variant="body2">{med}</Typography>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        )}
      </div>
      {/* )} */}

      {/* Immunizations */}
      {/* {history.immunizations && history.immunizations.length > 0 && ( */}
      <div>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="subtitle1" fontWeight="bold">
            Immunizations
          </Typography>
          <CopilotSectionEditedBadge section="immunizations" />
          {!editingSections?.immunizations && !readOnly && (
            <IconButton
              size="small"
              onClick={() => handleEdit("immunizations")}
            >
              <Edit fontSize="small" />
            </IconButton>
          )}
          {!readOnly &&
            hasLinkedEncounter &&
            !editingSections.immunizations &&
            hasImmunizationData(editedContent.immunizations) &&
            shouldShowHistoryAccept("immunizations") && (
              <Button
                startIcon={<AutoAwesome />}
                size="small"
                variant="outlined"
                sx={{ ml: "auto" }}
                disabled={populatingStates["immunizations"]}
                onClick={() => {
                  setPopulatingStates((prev) => ({
                    ...prev,
                    immunizations: true,
                  }));
                  handlePopulate(
                    () =>
                      CopilotManager.populateImmunizations(
                        encounterReference?.identifier,
                        organizationReference?.identifier,
                        countryCode,
                      ),
                    "immunizations",
                    [],
                    "copilot_refresh_immunizations",
                  );
                }}
              >
                {populatingStates["immunizations"] ? "Accepting..." : "Accept"}
              </Button>
            )}
        </Box>
        {editingSections?.immunizations ? (
          <Box sx={{ mt: 1 }}>
            {editedContent.immunizations?.map((item: string, index: number) => (
              <Box key={index} sx={{ display: "flex", gap: 1, mb: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  value={item}
                  onChange={(e) => {
                    const newImmunizations = [...editedContent.immunizations];
                    newImmunizations[index] = e.target.value;
                    setEditedContent((prev) => ({
                      ...prev,
                      immunizations: newImmunizations,
                    }));
                  }}
                />
                <IconButton
                  size="small"
                  onClick={() => {
                    const newImmunizations = editedContent.immunizations.filter(
                      (_: any, i: number) => i !== index,
                    );
                    setEditedContent((prev) => ({
                      ...prev,
                      immunizations: newImmunizations,
                    }));
                  }}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Box>
            ))}
            <Button
              size="small"
              onClick={() => {
                setEditedContent((prev) => ({
                  ...prev,
                  immunizations: [...(prev.immunizations || []), ""],
                }));
              }}
            >
              Add Item
            </Button>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1,
                mt: 1,
              }}
            >
              <Button
                size="small"
                onClick={() => handleCancel("immunizations")}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <LoadingButton
                size="small"
                variant="contained"
                onClick={() => handleSave("immunizations")}
                loading={isSaving}
              >
                Save
              </LoadingButton>
            </Box>
          </Box>
        ) : (
          <div>
            {history.immunizations && history.immunizations.length > 0 ? (
              <ul style={{ margin: 0, paddingLeft: "1.5rem" }}>
                {history.immunizations?.map((item, index) => (
                  <li key={index}>
                    <Typography variant="body2">{item}</Typography>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        )}
      </div>
      {/* )} */}

      {/* )} */}
    </Box>
  );
};

const extractReviewOfSystemsText = (
  reviewOfSystems: TCopilotReviewOfSystems | null,
  readOnly = false,
) => {
  const {
    copilotData,
    setCopilotData,
    encounterReference,
    patientReference,
    encounterHeaders,
    hasLinkedEncounter,
  } = useCopilotClinicalContext();
  const { setNotification } = useNotifications();
  const practitionerReference = usePractitionerReference();
  const organizationReference = useOrganizationReference();
  const [isEditing, setIsEditing] = useState(false);
  useUnsavedWhileEditing("copilot-note-review-of-systems", isEditing);
  const [editedContent, setEditedContent] = useState<TCopilotReviewOfSystems>(
    reviewOfSystems || {},
  );
  const [isSaving, setIsSaving] = useState(false);

  const hasAnyData =
    reviewOfSystems &&
    Object.values(reviewOfSystems).some(
      (value) => value !== null && value !== "",
    );

  const systemLabels: Record<keyof TCopilotReviewOfSystems, string> = {
    general: "General",
    heent: "HEENT",
    cardiovascular: "Cardiovascular",
    respiratory: "Respiratory",
    gastrointestinal: "Gastrointestinal",
    genitourinary: "Genitourinary",
    musculoskeletal: "Musculoskeletal",
    dermatological: "Dermatological",
    neurological: "Neurological",
    psychiatric: "Psychiatric",
    endocrine: "Endocrine",
    hematologicLymphatic: "Hematologic/Lymphatic",
    other: "Other",
  };

  const handlePopulate = async () => {
    if (!patientReference || !encounterReference || !practitionerReference) {
      setNotification({
        message:
          "Link this note to a medical encounter to apply content to the chart.",
        variant: "error",
      });
      throw new Error("Encounter not linked");
    }

    try {
      await CopilotManager.populateReviewOfSystems(
        editedContent,
        patientReference,
        encounterReference,
        practitionerReference,
        copilotData?.id,
      );
      setNotification({
        message: "Review of Systems populated successfully",
        variant: "success",
      });
    } catch (error: any) {
      console.error("Error populating review of systems:", error);
      setNotification({
        message: `Failed to populate review of systems: ${error?.message || "Unknown error"}`,
        variant: "error",
      });
      throw error;
    }
  };

  const handleSave = async () => {
    if (!copilotData?.id) return;

    try {
      setIsSaving(true);

      const updatedNote: TCopilot = withSectionHumanEditedExtension(
        {
          ...copilotData,
          note: {
            ...copilotData.note,
            reviewOfSystems: editedContent,
          },
        },
        "review-of-systems",
      );

      const response = await medicalsApi.updateCopilotNote(
        copilotData.id,
        updatedNote,
        encounterHeaders,
      );

      setCopilotData(response.data);
      setNotification({
        message: "Review of Systems updated successfully",
        variant: "success",
      });
      setIsEditing(false);
    } catch (error) {
      console.error(error);
      setNotification({
        message: "Failed to update Review of Systems",
        variant: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <Box display="flex" alignItems="center" gap={1}>
        <Typography variant="h6">Review of Systems</Typography>
        <CopilotSectionEditedBadge section="review-of-systems" />
        {!isEditing && !readOnly && (
          <IconButton size="small" onClick={() => setIsEditing(true)}>
            <Edit fontSize="small" />
          </IconButton>
        )}
        {!isEditing && !readOnly && hasLinkedEncounter && hasAnyData && (
          <CopilotSectionAcceptButton
            sections={["review-of-systems"]}
            populationStatusField="reviewOfSystems"
            sx={{ ml: "auto" }}
            onAccept={handlePopulate}
          />
        )}
      </Box>
      {isEditing ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
          {Object.entries(systemLabels)?.map(([key, label]) => (
            <TextField
              key={key}
              fullWidth
              multiline
              minRows={2}
              size="small"
              label={label}
              value={editedContent[key as keyof TCopilotReviewOfSystems] || ""}
              onChange={(e) =>
                setEditedContent((prev) => ({
                  ...prev,
                  [key]: e.target.value,
                }))
              }
            />
          ))}

          <Box
            sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 1 }}
          >
            <Button
              size="small"
              onClick={() => {
                setEditedContent(reviewOfSystems || {});
                setIsEditing(false);
              }}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <LoadingButton
              size="small"
              variant="contained"
              onClick={handleSave}
              loading={isSaving}
            >
              Save
            </LoadingButton>
          </Box>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
          {Object.entries(systemLabels)?.map(([key, label]) => {
            const value =
              reviewOfSystems?.[key as keyof TCopilotReviewOfSystems];
            return (
              <div key={key}>
                <Typography variant="subtitle2" fontWeight="bold">
                  {label}:
                </Typography>
                {value && <Typography variant="body2">{value}</Typography>}
              </div>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

const extractPhysicalAssessmentText = (
  physicalAssessment: TCopilotPhysicalAssessment | null,
  readOnly = false,
) => {
  const {
    copilotData,
    setCopilotData,
    encounterReference,
    patientReference,
    encounterHeaders,
    hasLinkedEncounter,
  } = useCopilotClinicalContext();
  const { setNotification } = useNotifications();
  const [isEditing, setIsEditing] = useState(false);
  useUnsavedWhileEditing("copilot-note-physical-assessment", isEditing);
  const [editedContent, setEditedContent] =
    useState<TCopilotPhysicalAssessment>({
      general: physicalAssessment?.general || null,
      headAndNeck: physicalAssessment?.headAndNeck || null,
      eyes: physicalAssessment?.eyes || null,
      cardiovascular: physicalAssessment?.cardiovascular || null,
      respiratory: physicalAssessment?.respiratory || null,
      gastrointestinal: physicalAssessment?.gastrointestinal || null,
      genitourinary: physicalAssessment?.genitourinary || null,
      musculoskeletal: physicalAssessment?.musculoskeletal || null,
      extremities: physicalAssessment?.extremities || null,
      neurological: physicalAssessment?.neurological || null,
      psychiatric: physicalAssessment?.psychiatric || null,
      skin: physicalAssessment?.skin || null,
      lymphatic: physicalAssessment?.lymphatic || null,
      other: physicalAssessment?.other || null,
    });
  const [isSaving, setIsSaving] = useState(false);
  const practitionerReference = usePractitionerReference();
  const assessmentLabels: Record<keyof TCopilotPhysicalAssessment, string> = {
    general: "General",
    headAndNeck: "Head and Neck",
    eyes: "Eyes",
    cardiovascular: "Cardiovascular",
    respiratory: "Respiratory",
    gastrointestinal: "Gastrointestinal",
    genitourinary: "Genitourinary",
    musculoskeletal: "Musculoskeletal",
    extremities: "Extremities",
    neurological: "Neurological",
    psychiatric: "Psychiatric",
    skin: "Skin",
    lymphatic: "Lymphatic",
    other: "Other",
  };

  const handlePopulate = async () => {
    if (!patientReference || !encounterReference || !practitionerReference) {
      setNotification({
        message:
          "Link this note to a medical encounter to apply content to the chart.",
        variant: "error",
      });
      throw new Error("Encounter not linked");
    }

    try {
      await CopilotManager.populatePhysicalAssessment(
        editedContent,
        patientReference,
        encounterReference,
        practitionerReference,
        copilotData?.id,
      );
      setNotification({
        message: "Physical Assessment populated successfully",
        variant: "success",
      });
    } catch (error: any) {
      console.error("Error populating physical assessment:", error);
      setNotification({
        message: `Failed to populate physical assessment: ${error?.message || "Unknown error"}`,
        variant: "error",
      });
      throw error;
    }
  };

  const handleSave = async () => {
    if (!copilotData?.id) return;

    try {
      setIsSaving(true);

      const updatedNote: TCopilot = withSectionHumanEditedExtension(
        {
          ...copilotData,
          note: {
            ...copilotData.note,
            physicalAssessment: editedContent,
          },
        },
        "physical-assessment",
      );

      const response = await medicalsApi.updateCopilotNote(
        copilotData.id,
        updatedNote,
        encounterHeaders,
      );

      setCopilotData(response.data);
      setNotification({
        message: "Physical Assessment updated successfully",
        variant: "success",
      });
      setIsEditing(false);
    } catch (error) {
      console.error(error);
      setNotification({
        message: "Failed to update Physical Assessment",
        variant: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const hasAnyData =
    physicalAssessment &&
    Object.values(physicalAssessment).some(
      (value) => value !== null && value !== "",
    );

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1}>
        <Typography variant="h6">Physical Assessment</Typography>
        <CopilotSectionEditedBadge section="physical-assessment" />
        {!isEditing && !readOnly && (
          <IconButton size="small" onClick={() => setIsEditing(!isEditing)}>
            <Edit fontSize="small" />
          </IconButton>
        )}
        {!isEditing && !readOnly && hasLinkedEncounter && hasAnyData && (
          <CopilotSectionAcceptButton
            sections={["physical-assessment"]}
            populationStatusField="physicalAssessment"
            sx={{ ml: "auto" }}
            onAccept={handlePopulate}
          />
        )}
      </Box>

      {isEditing ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
          {Object.entries(assessmentLabels)?.map(([key, label]) => (
            <TextField
              key={key}
              fullWidth
              multiline
              minRows={2}
              size="small"
              label={label}
              value={
                editedContent[key as keyof TCopilotPhysicalAssessment] || ""
              }
              onChange={(e) =>
                setEditedContent((prev) => ({
                  ...prev,
                  [key]: e.target.value,
                }))
              }
            />
          ))}

          <Box
            sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 1 }}
          >
            <Button
              size="small"
              onClick={() => {
                setEditedContent({
                  general: physicalAssessment?.general || null,
                  headAndNeck: physicalAssessment?.headAndNeck || null,
                  eyes: physicalAssessment?.eyes || null,
                  cardiovascular: physicalAssessment?.cardiovascular || null,
                  respiratory: physicalAssessment?.respiratory || null,
                  gastrointestinal:
                    physicalAssessment?.gastrointestinal || null,
                  genitourinary: physicalAssessment?.genitourinary || null,
                  musculoskeletal: physicalAssessment?.musculoskeletal || null,
                  extremities: physicalAssessment?.extremities || null,
                  neurological: physicalAssessment?.neurological || null,
                  psychiatric: physicalAssessment?.psychiatric || null,
                  skin: physicalAssessment?.skin || null,
                  lymphatic: physicalAssessment?.lymphatic || null,
                  other: physicalAssessment?.other || null,
                });
                setIsEditing(false);
              }}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <LoadingButton
              size="small"
              variant="contained"
              onClick={handleSave}
              loading={isSaving}
            >
              Save
            </LoadingButton>
          </Box>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
          {Object.entries(assessmentLabels)?.map(([key, label]) => {
            const value =
              physicalAssessment?.[key as keyof TCopilotPhysicalAssessment];
            return (
              <Box key={key}>
                <Typography variant="subtitle2" fontWeight="bold">
                  {label}:
                </Typography>
                {value && <Typography variant="body2">{value}</Typography>}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

type CopilotNoteProps = {
  note?: TCopilotNote;
  readOnly?: boolean;
};

const populateChiefComplaintSection = async (
  encounterReference: Reference | undefined,
  organizationReference: Reference | undefined,
  practitionerReference: Reference | undefined,
  patientReference: Reference | undefined,
  executeCallback: any,
  setNotification: any,
  countryCode?: string,
  section: "chiefComplaint" | "hpi" = "chiefComplaint",
) => {
  if (
    !encounterReference?.identifier ||
    !organizationReference?.identifier ||
    !practitionerReference?.identifier
  ) {
    setNotification({
      message:
        "Link this note to a medical encounter to apply content to the chart.",
      variant: "error",
    });
    throw new Error("Encounter not linked");
  }

  try {
    const populateFn =
      section === "hpi"
        ? CopilotManager.populateHpi
        : CopilotManager.populateChiefComplaint;

    await populateFn(
      encounterReference.identifier,
      organizationReference.identifier,
      practitionerReference.identifier,
      countryCode,
    );

    if (patientReference?.identifier) {
      await executeCallback(
        "copilot_refresh_chief_complaint",
        null,
        patientReference.identifier,
        encounterReference.identifier,
      );
    }

    setNotification({
      message:
        section === "hpi"
          ? "HPI populated successfully"
          : "Chief complaint populated successfully",
      variant: "success",
    });
  } catch (error) {
    setNotification({
      message:
        section === "hpi"
          ? "Failed to populate HPI"
          : "Failed to populate chief complaint",
      variant: "error",
    });
    throw error;
  }
};

function CopilotNoteDisplay({ note, readOnly }: CopilotNoteProps) {
  const { encounterReference, patientReference, hasLinkedEncounter } =
    useCopilotClinicalContext();
  const practitionerReference = usePractitionerReference();
  const organizationReference = useOrganizationReference();
  const { countryCode } = useOrganization();
  const { setNotification } = useNotifications();
  const { executeCallback } = useExecutor();

  if (!note) return null;

  const populateChiefComplaintHandler = hasLinkedEncounter
    ? async () => {
        await populateChiefComplaintSection(
          encounterReference,
          organizationReference,
          practitionerReference,
          patientReference,
          executeCallback,
          setNotification,
          countryCode,
          "chiefComplaint",
        );
      }
    : undefined;

  const populateHpiHandler = hasLinkedEncounter
    ? async () => {
        await populateChiefComplaintSection(
          encounterReference,
          organizationReference,
          practitionerReference,
          patientReference,
          executeCallback,
          setNotification,
          countryCode,
          "hpi",
        );
      }
    : undefined;

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <EditableSection
        title="Chief Complaint"
        content={note.chiefComplaint}
        renderContent={(content, readOnly) => (
          <Typography variant="body2">{content}</Typography>
        )}
        editable={!readOnly}
        field="chiefComplaint"
        onPopulate={populateChiefComplaintHandler}
      />

      <EditableSection
        title="HPC / HPI"
        content={note.hpi}
        renderContent={(content, readOnly) => (
          <Typography variant="body2">{content}</Typography>
        )}
        editable={!readOnly}
        field="hpi"
        onPopulate={populateHpiHandler}
      />

      <Box>
        <EditableVitals vitals={note?.vitals} readOnly={readOnly} />
      </Box>

      <Box>
        <EditableAllergies
          allergies={note.history?.allergies}
          readOnly={readOnly}
        />
      </Box>

      <EditableSection
        title="Patient History"
        content={note.history || {}}
        renderContent={extractHistoryText}
        editable={!readOnly}
        editOnTitle={false}
        field="history"
      />

      <EditableSection
        content={note.reviewOfSystems || []}
        renderContent={extractReviewOfSystemsText}
        editable={!readOnly}
        field="reviewOfSystems"
      />

      <EditableSection
        content={note.physicalAssessment || {}}
        renderContent={extractPhysicalAssessmentText}
        editable={!readOnly}
        field="physicalAssessment"
      />

      <EditableSection
        content={note.diagnoses || []}
        renderContent={extractDiagnosisText}
        editable={!readOnly}
        field="diagnoses"
      />

      <EditableSection
        content={note.plan || {}}
        renderContent={extractPlanText}
        editable={!readOnly}
        field="plan"
      />
    </Box>
  );
}

export default CopilotNoteDisplay;
